import { atom } from "jotai";
import type {
  TriageActiveStage,
  TriageChecklistItem,
  TriageKbLink,
  TriagePhaseType,
} from "shared";
import type { HelenaOpenSource } from "../analytics/helenaAnalytics";

// ─── Types ───────────────────────────────────────────────────────────

export type HelenaV2Tab = "agents" | "solution" | "compose" | "sentiment";
export type ComposeSubTab = "ai_suggested" | "compose_prompt";
export type HelenaV2Path =
  | "root"
  | "vendorDocuments"
  | "similarTickets"
  | "knowledgeBase"
  | "whyThisSuggestion";

// ─── Navigation Path ─────────────────────────────────────────────────

const helenaV2PathAtom = atom<HelenaV2Path>("root");

export const helenaV2Path = atom((get) => get(helenaV2PathAtom));
export const setHelenaV2Path = atom(null, (_get, set, value: HelenaV2Path) => {
  set(helenaV2PathAtom, value);
});

// ─── Active Tab ──────────────────────────────────────────────────────

const helenaV2ActiveTabAtom = atom<HelenaV2Tab>("agents");

export const helenaV2ActiveTab = atom((get) => get(helenaV2ActiveTabAtom));
export const setHelenaV2ActiveTab = atom(
  null,
  (_get, set, value: HelenaV2Tab) => {
    set(helenaV2ActiveTabAtom, value);
  },
);

// ─── Compose Sub-Tab ─────────────────────────────────────────────────

const composeSubTabAtom = atom<ComposeSubTab>("ai_suggested");

export const composeSubTab = atom((get) => get(composeSubTabAtom));
export const setComposeSubTab = atom(
  null,
  (_get, set, value: ComposeSubTab) => {
    set(composeSubTabAtom, value);
  },
);

// ─── Compose Prompt Text ─────────────────────────────────────────────

const composePromptTextAtom = atom<string>("");

export const composePromptText = atom((get) => get(composePromptTextAtom));
export const setComposePromptText = atom(null, (_get, set, value: string) => {
  set(composePromptTextAtom, value);
});

// ─── Compose Pending Prompt (optimistic, pre-API turn) ──────────────

const composePendingPromptAtom = atom<string | null>(null);

export const composePendingPrompt = atom((get) =>
  get(composePendingPromptAtom),
);
export const setComposePendingPrompt = atom(
  null,
  (_get, set, value: string | null) => {
    set(composePendingPromptAtom, value);
  },
);

// ─── Take Control Button Visibility ─────────────────────────────────

/**
 * Owned end-to-end by AgentsTab: it publishes the flag from the run's status and
 * clears it when it unmounts. Deliberately NOT part of `resetHelenaV2Store` —
 * React fires child effects before parent ones, so the panel's own mount/switch
 * reset would land AFTER AgentsTab has published and wipe it, and AgentsTab,
 * whose inputs never changed, would never publish again. The button would then
 * stay hidden for the rest of a live run.
 */
const showTakeControlButtonAtom = atom<boolean>(false);

export const showTakeControlButton = atom((get) =>
  get(showTakeControlButtonAtom),
);
export const setShowTakeControlButton = atom(
  null,
  (_get, set, value: boolean) => {
    set(showTakeControlButtonAtom, value);
  },
);

// ─── Solution Feedback Bar Dismissal ────────────────────────────────
// Lives in the store (not component state) so dismissing survives tab
// switches, which unmount SolutionTab. Reset on ticket change.

const solutionFeedbackDismissedAtom = atom<boolean>(false);

export const solutionFeedbackDismissed = atom((get) =>
  get(solutionFeedbackDismissedAtom),
);
export const setSolutionFeedbackDismissed = atom(
  null,
  (_get, set, value: boolean) => {
    set(solutionFeedbackDismissedAtom, value);
  },
);

// ─── Solution Feedback Bar Reveal ───────────────────────────────────
// Flipped once the post-response delay has elapsed. Stored (not component
// state) so the bar stays revealed across tab switches, which unmount it —
// the wait happens only once per ticket.

const solutionFeedbackRevealedAtom = atom<boolean>(false);

export const solutionFeedbackRevealed = atom((get) =>
  get(solutionFeedbackRevealedAtom),
);
export const setSolutionFeedbackRevealed = atom(
  null,
  (_get, set, value: boolean) => {
    set(solutionFeedbackRevealedAtom, value);
  },
);

// ─── Solution Feedback Bar Visibility ───────────────────────────────
// Published by the (overlaying) feedback bar so SolutionTab can pad its
// scroll area and keep the last card reachable.

const solutionFeedbackVisibleAtom = atom<boolean>(false);

export const solutionFeedbackVisible = atom((get) =>
  get(solutionFeedbackVisibleAtom),
);
export const setSolutionFeedbackVisible = atom(
  null,
  (_get, set, value: boolean) => {
    set(solutionFeedbackVisibleAtom, value);
  },
);

// ─── Panel Open Source (analytics) ──────────────────────────────────
// How the currently open panel was reached, so close/expand/engage events can
// be attributed to the route that opened them. Set immediately BEFORE the open
// itself — which is why it is deliberately NOT cleared by resetHelenaV2Store:
// HelenaV2 calls that reset from its own mount effect, i.e. after the opener has
// already recorded the source. useHelenaPanelMode clears it per ticket instead.

const helenaPanelOpenSourceAtom = atom<HelenaOpenSource | null>(null);

export const helenaPanelOpenSource = atom((get) =>
  get(helenaPanelOpenSourceAtom),
);
export const setHelenaPanelOpenSource = atom(
  null,
  (_get, set, value: HelenaOpenSource | null) => {
    set(helenaPanelOpenSourceAtom, value);
  },
);

// ─── Triage Active Stage (transient MQTT "started" events) ──────────

const triageActiveStageAtom = atom<TriageActiveStage | null>(null);

export const triageActiveStage = atom((get) => get(triageActiveStageAtom));
export const setTriageActiveStage = atom(
  null,
  (_get, set, value: TriageActiveStage | null) => {
    set(triageActiveStageAtom, value);
  },
);

// ─── Manual Review Draft ─────────────────────────────────────────────

/** Sections the technician dismissed via the header ✕ — omitted from submit. */
export interface ReviewDroppedSections {
  tags: boolean;
  kb: boolean;
  checklist: boolean;
}

/**
 * The technician's working copy of a held phase's proposal. Seeded from
 * `pending_review.proposed_changes` and edited in place; the original stays
 * untouched in the query cache so per-field undo needs no second copy.
 */
export interface ReviewDraft {
  phase: TriagePhaseType;
  /** Mutable copy of `proposed_changes.details` — includes unrendered keys. */
  details: Record<string, unknown>;
  techExtId: string | null;
  techName: string | null;
  kbLinks: TriageKbLink[];
  checklists: TriageChecklistItem[];
  droppedSections: ReviewDroppedSections;
  prioritySelections: Record<string, string>;
}

const reviewDraftAtom = atom<ReviewDraft | null>(null);

export const reviewDraft = atom((get) => get(reviewDraftAtom));

export const setReviewDraft = atom(
  null,
  (_get, set, value: ReviewDraft | null) => {
    set(reviewDraftAtom, value);
  },
);

/** Patches a single `details` key without clobbering the rest of the proposal. */
export const patchReviewDetail = atom(
  null,
  (get, set, patch: { key: string; value: unknown }) => {
    const draft = get(reviewDraftAtom);
    if (!draft) return;
    set(reviewDraftAtom, {
      ...draft,
      details: { ...draft.details, [patch.key]: patch.value },
    });
  },
);

export const selectReviewPriority = atom(
  null,
  (
    get,
    set,
    patch: {
      questionId: string;
      optionId: string;
      field: string;
      value: unknown;
    },
  ) => {
    const draft = get(reviewDraftAtom);
    if (!draft) return;
    set(reviewDraftAtom, {
      ...draft,
      details: { ...draft.details, [patch.field]: patch.value },
      prioritySelections: {
        ...draft.prioritySelections,
        [patch.questionId]: patch.optionId,
      },
    });
  },
);

export const patchReviewDraft = atom(
  null,
  (get, set, patch: Partial<Omit<ReviewDraft, "phase">>) => {
    const draft = get(reviewDraftAtom);
    if (!draft) return;
    set(reviewDraftAtom, { ...draft, ...patch });
  },
);

/**
 * Clears the draft when the incoming hold is for a different phase (or the hold
 * is gone entirely). Reads the current draft internally so callers — notably the
 * MQTT handler — never have to subscribe to it, which would otherwise re-run
 * their effect on every keystroke the technician types.
 */
export const resetReviewDraftForPhase = atom(
  null,
  (get, set, incomingPhase: TriagePhaseType | null) => {
    const draft = get(reviewDraftAtom);
    if (!draft) return;
    if (draft.phase !== incomingPhase) set(reviewDraftAtom, null);
  },
);

/** Drops (or restores) a whole section — tags / KB / checklist. */
export const toggleReviewSection = atom(
  null,
  (
    get,
    set,
    patch: { section: keyof ReviewDroppedSections; dropped: boolean },
  ) => {
    const draft = get(reviewDraftAtom);
    if (!draft) return;
    set(reviewDraftAtom, {
      ...draft,
      droppedSections: {
        ...draft.droppedSections,
        [patch.section]: patch.dropped,
      },
    });
  },
);

// ─── Reset ───────────────────────────────────────────────────────────

export const resetHelenaV2Store = atom(null, (_get, set) => {
  set(helenaV2PathAtom, "root");
  set(helenaV2ActiveTabAtom, "agents");
  set(composeSubTabAtom, "ai_suggested");
  set(composePromptTextAtom, "");
  set(composePendingPromptAtom, null);
  set(triageActiveStageAtom, null);
  set(solutionFeedbackDismissedAtom, false);
  set(solutionFeedbackRevealedAtom, false);
  set(solutionFeedbackVisibleAtom, false);
  set(reviewDraftAtom, null);
});
